import type { ColumnDef } from "@gately/ui/data-table";
import { useEffect, useMemo, useState } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Building2,
  CircleCheck,
  CircleDashed,
  KeyRound,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from "lucide-react";
import { z } from "zod/v4";

import { cn } from "@gately/ui";
import { Avatar } from "@gately/ui/avatar";
import { Button } from "@gately/ui/button";
import { Card } from "@gately/ui/card";
import { ConfirmDialog } from "@gately/ui/confirm-dialog";
import { DataTable, SortableHeader } from "@gately/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { SettingsSection } from "@gately/ui/settings";
import { StatusBadge } from "@gately/ui/status-badge";
import { toast } from "@gately/ui/toast";

import { BulkImportDialog } from "~/components/units/bulk-import-dialog";
import { orpc } from "~/lib/orpc";
import { useMe } from "~/lib/use-me";
import { useTerms } from "~/lib/use-terms";

const searchSchema = z.object({
  q: z.string().min(1).optional().catch(undefined),
});

export const Route = createFileRoute("/_dashboard/reglages/unites")({
  validateSearch: (s) => searchSchema.parse(s),
  // Prefetch so useSuspenseQuery has data ready and never suspends mid-navigation.
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        orpc.unit.list.queryOptions({ input: { limit: 500 } }),
      ),
      // No-arg on purpose: the owner picker needs the full roster, unfiltered.
      context.queryClient.ensureQueryData(orpc.account.list.queryOptions()),
    ]),
  component: UnitsPage,
});

function unitLabel(
  u: {
    name?: string | null;
    building: string | null;
    number: string;
  },
  isOffice: boolean,
): string {
  if (u.name) return u.name;
  // "Apt" is a residence word; an unnamed office unit is just "B 12".
  if (isOffice) return u.building ? `${u.building} ${u.number}` : u.number;
  return u.building
    ? `Bât. ${u.building} — Apt ${u.number}`
    : `Apt ${u.number}`;
}

const VACANT = "__vacant__";

/** A KPI tile: label + tinted icon chip, big value, a colored hint line. */
function Kpi({
  label,
  value,
  hint,
  tone,
  icon,
}: {
  label: string;
  value: number;
  hint: string;
  tone: "neutral" | "success" | "warning" | "info";
  icon: React.ReactNode;
}) {
  const chip = {
    neutral: "bg-muted text-muted-foreground",
    success: "bg-success-tint text-success",
    warning: "bg-warning-tint text-(--warning-700)",
    info: "bg-info-tint text-info",
  }[tone];
  const hintColor = {
    neutral: "text-muted-foreground",
    success: "text-success",
    warning: "text-(--warning-700)",
    info: "text-info",
  }[tone];
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground text-sm font-medium">
          {label}
        </span>
        <span
          className={cn(
            "flex size-8 items-center justify-center rounded-lg",
            chip,
          )}
        >
          {icon}
        </span>
      </div>
      <div className="mt-3.5 text-[2rem] leading-none font-bold tracking-tight">
        {value}
      </div>
      <div className={cn("mt-2.5 text-xs font-medium", hintColor)}>{hint}</div>
    </Card>
  );
}

/** Round avatar for an owner; vacant renders a muted dashed placeholder. */
function OwnerAvatar({ name }: { name: string | null }) {
  if (!name) {
    return (
      <span className="bg-muted text-muted-foreground inline-flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
        —
      </span>
    );
  }
  return <Avatar name={name} className="size-7 text-[10px]" />;
}

function UnitsPage() {
  const queryClient = useQueryClient();
  const me = useMe();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  // Debounced free-text search: local state → URL after 300 ms.
  const [q, setQ] = useState(search.q ?? "");
  useEffect(() => {
    const t = setTimeout(() => {
      const next = q.trim() || undefined;
      if (next !== search.q) {
        void navigate({
          search: (prev) => ({ ...prev, q: next }),
          replace: true,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [q, search.q, navigate]);

  const unitsQuery = useQuery({
    ...orpc.unit.list.queryOptions({
      input: { limit: 500, ...(search.q ? { q: search.q } : {}) },
    }),
    placeholderData: keepPreviousData,
  });
  const units = unitsQuery.data ?? [];
  const { data: accounts } = useSuspenseQuery(orpc.account.list.queryOptions());
  const eligibleOwners = useMemo(
    () =>
      accounts.filter((a) => a.role === "proprietaire" || a.role === "syndic"),
    [accounts],
  );
  const terms = useTerms();
  const isOffice = me.activeOrg?.type === "office";

  const [name, setName] = useState("");
  const [building, setBuilding] = useState("");
  const [number, setNumber] = useState("");
  const [floor, setFloor] = useState("");
  const [ownerId, setOwnerId] = useState(VACANT);
  const [toDelete, setToDelete] = useState<
    { id: string; label: string } | undefined
  >(undefined);
  const [importOpen, setImportOpen] = useState(false);
  // A reassignment held back for confirmation because the unit is rented —
  // the owner <Select> is controlled by the row, so it stays on the current
  // owner until the mutation lands (or the syndic cancels).
  const [ownerChange, setOwnerChange] = useState<
    { id: string; ownerId: string | null } | undefined
  >(undefined);
  // Edit dialog — identity fields only (name/building/number/floor). The
  // owner is reassigned inline from the table, so it stays out of this form.
  const [editing, setEditing] = useState<
    | {
        id: string;
        name: string;
        building: string;
        number: string;
        floor: string;
      }
    | undefined
  >(undefined);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: orpc.unit.list.key() });

  const createUnit = useMutation(
    orpc.unit.create.mutationOptions({
      onSuccess: async () => {
        toast.success(isOffice ? "Entreprise créée." : "Unité créée.");
        setName("");
        setBuilding("");
        setNumber("");
        setFloor("");
        setOwnerId(VACANT);
        await invalidate();
      },
      onError: (err) =>
        toast.error(
          err.message ||
            (isOffice
              ? "Impossible de créer l'entreprise."
              : "Impossible de créer l'unité."),
        ),
    }),
  );
  const setOwner = useMutation(
    orpc.unit.setOwner.mutationOptions({
      // Both arms clear the confirm (ConfirmDialog's contract): closing only
      // on success would strand the dialog open over a failed reassignment.
      onSuccess: async () => {
        toast.success(`${terms.ownerLabel} mis à jour.`);
        setOwnerChange(undefined);
        await invalidate();
      },
      onError: (err) => {
        setOwnerChange(undefined);
        toast.error(err.message || "Échec de la mise à jour.");
      },
    }),
  );
  const updateUnit = useMutation(
    orpc.unit.update.mutationOptions({
      onSuccess: async () => {
        toast.success(
          isOffice ? "Entreprise mise à jour." : "Unité mise à jour.",
        );
        setEditing(undefined);
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Échec de la mise à jour."),
    }),
  );
  const deleteUnit = useMutation(
    orpc.unit.delete.mutationOptions({
      onSuccess: async () => {
        toast.success(isOffice ? "Entreprise supprimée." : "Unité supprimée.");
        setToDelete(undefined);
        await invalidate();
      },
      onError: (err) => toast.error(err.message || "Échec de la suppression."),
    }),
  );

  const total = units.length;
  // The tiles partition the table exactly as the Statut column does: a rented
  // unit reads « En location », not « Occupée », so it is counted once and in
  // one place — a card that said 8 while its facet returned 5 rows would be
  // read as a bug. An office org has no occupancy rows at all, so `rented`
  // stays 0 there and the other two fall back to the old owner/no-owner split.
  const isRented = (u: { rented: boolean }) => !isOffice && u.rented;
  const rented = units.filter(isRented).length;
  const occupied = units.filter((u) => u.ownerId && !isRented(u)).length;
  const vacant = total - occupied - rented;
  const buildings = new Set(units.map((u) => u.building).filter(Boolean)).size;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    createUnit.mutate({
      number,
      ...(name ? { name } : {}),
      ...(building ? { building } : {}),
      ...(floor ? { floor } : {}),
      ...(ownerId !== VACANT ? { ownerId } : {}),
    });
  }

  type UnitRow = (typeof units)[number];

  function submitEdit(e: React.FormEvent) {
    e.preventDefault();
    if (!editing) return;
    const name = editing.name.trim();
    const number = editing.number.trim();
    if (!number) return;
    // An emptied name is omitted so the server writes NULL and the label
    // falls back to building + number.
    updateUnit.mutate({
      id: editing.id,
      number,
      building: editing.building.trim(),
      floor: editing.floor.trim(),
      ...(name ? { name } : {}),
    });
  }

  // Memoised: TanStack's flexRender treats each `cell` function as a component
  // type, so a fresh columns array on every render would remount every cell —
  // closing an open owner <Select> whenever the page re-renders.
  const columns: ColumnDef<UnitRow>[] = useMemo(
    () => [
      {
        id: "unit",
        accessorFn: (u) => unitLabel(u, isOffice),
        size: 260,
        minSize: 180,
        enableHiding: false,
        meta: {
          label: terms.unitRowLabel,
          filter: { kind: "text", placeholder: "Filtrer…" },
        },
        header: ({ column }) => (
          <SortableHeader column={column}>{terms.unitRowLabel}</SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <span className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
              <Building2 className="size-4.5" strokeWidth={1.75} />
            </span>
            <span className="truncate font-semibold">
              {unitLabel(row.original, isOffice)}
            </span>
          </div>
        ),
      },
      {
        accessorKey: "floor",
        size: 120,
        minSize: 90,
        meta: {
          label: "Étage",
          filter: { kind: "text", placeholder: "Étage" },
        },
        header: ({ column }) => (
          <SortableHeader column={column}>Étage</SortableHeader>
        ),
        cell: ({ getValue }) => (
          <span className="text-foreground/80">
            {getValue<string | null>() ?? "—"}
          </span>
        ),
      },
      {
        id: "owner",
        accessorKey: "ownerName",
        size: 260,
        minSize: 180,
        meta: {
          label: terms.ownerLabel,
          filter: { kind: "text", placeholder: "Filtrer…" },
        },
        header: ({ column }) => (
          <SortableHeader column={column}>{terms.ownerLabel}</SortableHeader>
        ),
        cell: ({ row }) => {
          const u = row.original;
          // The tenant only exists in the résidence vertical, and only while
          // a rental is live — `null` covers both, and a rented unit whose
          // tenant carries no name (never seen, but nullable) as well.
          const tenantName = !isOffice && u.rented ? u.tenantName : null;
          return (
            <div className="min-w-0">
              <Select
                value={u.ownerId ?? VACANT}
                onValueChange={(v) => {
                  const ownerId = v === VACANT ? null : v;
                  // A live rental survives the reassignment, so the syndic is
                  // told before it happens rather than after.
                  if (u.rented) setOwnerChange({ id: u.id, ownerId });
                  else setOwner.mutate({ id: u.id, ownerId });
                }}
                disabled={
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  setOwner.isPending && setOwner.variables?.id === u.id
                }
              >
                <SelectTrigger
                  size="sm"
                  className="hover:bg-muted data-[state=open]:bg-muted w-full border-transparent shadow-none"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel className="font-mono text-[10px] tracking-wider uppercase">
                      Attribuer à
                    </SelectLabel>
                    <SelectItem value={VACANT}>
                      <OwnerAvatar name={null} />
                      Vacant (aucun {terms.ownerLabel.toLowerCase()})
                    </SelectItem>
                    {eligibleOwners.map((p) => (
                      <SelectItem key={p.userId} value={p.userId}>
                        <OwnerAvatar name={p.name} />
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
              {tenantName ? (
                <div className="text-muted-foreground mt-1 truncate pl-3 text-xs">
                  Louée à {tenantName}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        id: "status",
        // Three states for a résidence, two for an office: an office org has
        // no rentals at all, so `rented` there is dead weight, not a state.
        accessorFn: (u) =>
          !isOffice && u.rented ? "rented" : u.ownerId ? "occupied" : "vacant",
        size: 150,
        minSize: 120,
        filterFn: "arrIncludesSome",
        meta: { label: "Statut" },
        header: ({ column }) => (
          <SortableHeader column={column}>Statut</SortableHeader>
        ),
        cell: ({ row }) => {
          const u = row.original;
          if (!isOffice && u.rented)
            return <StatusBadge tone="info">Louée</StatusBadge>;
          return u.ownerId ? (
            <StatusBadge tone="success">Occupée</StatusBadge>
          ) : (
            <StatusBadge tone="neutral">Vacante</StatusBadge>
          );
        },
      },
      {
        id: "actions",
        size: 110,
        minSize: 100,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        meta: { label: "Actions", align: "right" },
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1.5">
            <Button
              variant="outline"
              size="sm"
              aria-label={`Modifier ${unitLabel(row.original, isOffice)}`}
              className="size-8 p-0"
              onClick={() => {
                const u = row.original;
                setEditing({
                  id: u.id,
                  name: u.name ?? "",
                  building: u.building ?? "",
                  number: u.number,
                  floor: u.floor ?? "",
                });
              }}
            >
              <Pencil className="size-3.5" strokeWidth={1.85} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              aria-label={`Supprimer ${unitLabel(row.original, isOffice)}`}
              className="text-destructive size-8 p-0 hover:border-(--danger-500)/40 hover:bg-(--danger-50)"
              onClick={() =>
                setToDelete({
                  id: row.original.id,
                  label: unitLabel(row.original, isOffice),
                })
              }
            >
              <Trash2 className="size-3.5" strokeWidth={1.85} />
            </Button>
          </div>
        ),
      },
    ],
    [terms, isOffice, eligibleOwners, setOwner],
  );

  const nameField = (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="name">{isOffice ? "Nom (entreprise)" : "Nom"}</Label>
      <Input
        id="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={isOffice ? "Acme SARL" : "Villa Bougainvillier"}
      />
    </div>
  );

  return (
    <>
      <div className="mb-5 flex items-start justify-between gap-4">
        <SettingsSection
          title={terms.unitsNav}
          hint={`Occupation et ${terms.ownerPlural.toLowerCase()} de votre ${terms.orgNoun}, en un coup d'œil.`}
        />
        <Button variant="outline" onClick={() => setImportOpen(true)}>
          <Upload className="size-4" />
          Importer en masse
        </Button>
      </div>

      {/* KPI grid — a fourth tile (« En location ») only for résidences. */}
      <div
        className={cn(
          "grid grid-cols-1 gap-4",
          isOffice ? "sm:grid-cols-3" : "sm:grid-cols-2 lg:grid-cols-4",
        )}
      >
        <Kpi
          label={`Total ${terms.unitPlural}`}
          value={total}
          tone="neutral"
          icon={<Building2 className="size-4" strokeWidth={1.85} />}
          hint={
            buildings > 0
              ? `Réparties sur ${buildings} bâtiment${buildings > 1 ? "s" : ""}`
              : "Sur l'ensemble du site"
          }
        />
        <Kpi
          label="Occupées"
          value={occupied}
          tone="success"
          icon={<CircleCheck className="size-4" strokeWidth={1.85} />}
          // « attribué » would now overstate it for a résidence: a rented unit
          // has an owner attributed too, and is counted on the next tile.
          hint={`Avec un ${terms.ownerLabel.toLowerCase()} ${
            isOffice ? "attribué" : "résident"
          }`}
        />
        {isOffice ? null : (
          <Kpi
            label="En location"
            value={rented}
            tone="info"
            icon={<KeyRound className="size-4" strokeWidth={1.85} />}
            hint="Avec un locataire déclaré"
          />
        )}
        <Kpi
          label="Vacantes"
          value={vacant}
          tone="warning"
          icon={<CircleDashed className="size-4" strokeWidth={1.85} />}
          hint={`Sans ${terms.ownerLabel.toLowerCase()}`}
        />
      </div>

      {/* Create card */}
      <Card className="mt-4 p-5">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="text-brand flex size-8 shrink-0 items-center justify-center rounded-lg bg-orange-50">
            <Plus className="size-4" strokeWidth={2} />
          </span>
          <div>
            <div className="text-sm font-semibold">
              {isOffice ? "Ajouter une entreprise" : "Ajouter une unité"}
            </div>
            <div className="text-muted-foreground text-xs">
              Le numéro doit être unique au sein du bâtiment.
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="grid grid-cols-1 items-end gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[1fr_0.7fr_0.7fr_0.9fr_1.3fr_auto]"
        >
          {isOffice ? nameField : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="building">Bâtiment</Label>
            <Input
              id="building"
              value={building}
              onChange={(e) => setBuilding(e.target.value)}
              placeholder="Bloc A"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="number">
              Numéro <span className="text-brand">*</span>
            </Label>
            <Input
              id="number"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="12"
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="floor">Étage</Label>
            <Input
              id="floor"
              value={floor}
              onChange={(e) => setFloor(e.target.value)}
              placeholder="3 · Rez-de-chaussée"
            />
          </div>
          {isOffice ? null : nameField}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="owner">{terms.ownerLabel}</Label>
            <Select value={ownerId} onValueChange={setOwnerId}>
              <SelectTrigger id="owner" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel className="font-mono text-[10px] tracking-wider uppercase">
                    {terms.ownerPlural} & {terms.adminPlural.toLowerCase()}{" "}
                    éligibles
                  </SelectLabel>
                  <SelectItem value={VACANT}>
                    <OwnerAvatar name={null} />
                    Vacant (aucun {terms.ownerLabel.toLowerCase()})
                  </SelectItem>
                  {eligibleOwners.map((p) => (
                    <SelectItem key={p.userId} value={p.userId}>
                      <OwnerAvatar name={p.name} />
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={createUnit.isPending}>
            <Plus className="size-4" />
            {createUnit.isPending
              ? "Création…"
              : isOffice
                ? "Créer une entreprise"
                : "Ajouter l'unité"}
          </Button>
        </form>
      </Card>

      {/* Table */}
      <DataTable
        className="mt-4"
        columns={columns}
        data={units}
        isLoading={unitsQuery.isPending}
        pageSize={25}
        initialPinning={{ left: ["unit"], right: ["actions"] }}
        showColumnFilters
        showColumnToggle
        search={{
          value: q,
          onChange: setQ,
          placeholder: `Rechercher une ${terms.unitSingular}…`,
        }}
        filters={[
          {
            columnId: "status",
            label: "Statut",
            options: [
              { value: "occupied", label: "Occupée" },
              { value: "vacant", label: "Vacante" },
              ...(isOffice ? [] : [{ value: "rented", label: "Louée" }]),
            ],
          },
        ]}
        empty={{
          icon: <Building2 className="size-6" strokeWidth={1.75} />,
          title: search.q
            ? "Aucun résultat pour cette recherche."
            : `Aucune ${terms.unitSingular} pour l'instant`,
          description: search.q
            ? undefined
            : `Ajoutez votre première ${terms.unitSingular} avec le formulaire ci-dessus pour commencer à suivre l'occupation.`,
        }}
        footer={
          <span>
            {total} {terms.unitPlural} · {occupied} occupées
            {isOffice ? null : ` · ${rented} en location`} · {vacant} vacantes
          </span>
        }
      />

      <Dialog
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
      >
        <DialogContent>
          <form onSubmit={submitEdit}>
            <DialogHeader>
              <DialogTitle>
                {isOffice ? "Modifier l'entreprise" : "Modifier l'unité"}
              </DialogTitle>
              <DialogDescription>
                {isOffice
                  ? "Le nom de l'entreprise et son emplacement dans l'immeuble."
                  : "Le nom affiché, le bâtiment, le numéro et l'étage."}{" "}
                Le {terms.ownerLabel.toLowerCase()} se change depuis le tableau.
              </DialogDescription>
            </DialogHeader>
            {editing ? (
              <div className="my-4 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <Label htmlFor="edit-name">
                    {isOffice ? "Nom (entreprise)" : "Nom"}
                  </Label>
                  <Input
                    id="edit-name"
                    value={editing.name}
                    onChange={(e) =>
                      setEditing((s) =>
                        s ? { ...s, name: e.target.value } : s,
                      )
                    }
                    placeholder={
                      isOffice ? "Acme SARL" : "Villa Bougainvillier"
                    }
                    maxLength={120}
                    autoFocus
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-building">Bâtiment</Label>
                  <Input
                    id="edit-building"
                    value={editing.building}
                    onChange={(e) =>
                      setEditing((s) =>
                        s ? { ...s, building: e.target.value } : s,
                      )
                    }
                    placeholder="Bloc A"
                    maxLength={40}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="edit-number">
                    Numéro <span className="text-brand">*</span>
                  </Label>
                  <Input
                    id="edit-number"
                    value={editing.number}
                    onChange={(e) =>
                      setEditing((s) =>
                        s ? { ...s, number: e.target.value } : s,
                      )
                    }
                    placeholder="12"
                    maxLength={40}
                    required
                  />
                </div>
                <div className="flex flex-col gap-1.5 sm:col-span-2">
                  <Label htmlFor="edit-floor">Étage</Label>
                  <Input
                    id="edit-floor"
                    value={editing.floor}
                    onChange={(e) =>
                      setEditing((s) =>
                        s ? { ...s, floor: e.target.value } : s,
                      )
                    }
                    placeholder="3 · Rez-de-chaussée"
                    maxLength={40}
                  />
                </div>
              </div>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditing(undefined)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={updateUnit.isPending || !editing?.number.trim()}
              >
                {updateUnit.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={toDelete !== undefined}
        onOpenChange={(open) => {
          if (!open) setToDelete(undefined);
        }}
        icon={<Trash2 className="size-5" strokeWidth={1.85} />}
        title={`Supprimer cette ${terms.unitSingular} ?`}
        description={
          <>
            <b className="text-foreground/80 font-semibold">
              {toDelete?.label}
            </b>{" "}
            sera définitivement supprimée. Cette action est irréversible.
          </>
        }
        confirmLabel="Supprimer"
        pendingLabel="Suppression…"
        destructive
        pending={deleteUnit.isPending}
        onConfirm={() => {
          if (toDelete) deleteUnit.mutate({ id: toDelete.id });
        }}
      />

      <ConfirmDialog
        open={ownerChange !== undefined}
        onOpenChange={(open) => {
          if (!open) setOwnerChange(undefined);
        }}
        title={`Changer le ${terms.ownerLabel.toLowerCase()} malgré la location ?`}
        description="Cette unité est en location active. Changer le propriétaire ne met pas fin à la location."
        confirmLabel="Continuer"
        pendingLabel="Mise à jour…"
        pending={setOwner.isPending}
        onConfirm={() => {
          if (ownerChange) setOwner.mutate(ownerChange);
        }}
      />

      <BulkImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        terms={terms}
        onImported={async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: orpc.unit.list.key() }),
            queryClient.invalidateQueries({
              queryKey: orpc.account.list.key(),
            }),
          ]);
        }}
      />
    </>
  );
}
